import { useCallback, useMemo, useState } from 'react';
import type { LatLngTuple } from 'leaflet';
import { MAP_CLOSE_UP_ZOOM } from '@/components/map/map.constants';
import { findWaypointByKey, isStartWaypoint, type DayRoute } from '../utils/dayRoutes';
import { buildJourneyEntries, buildLegPointsByWaypointKey } from '../utils/journeyEntries';
import { getZoomToSeparateFromNeighbors } from '../utils/waypointGroups';

export type JourneyStepDirection = 1 | -1;

export interface FlyToRequest {
  points: LatLngTuple[];
  separatingZoom: number | null;
}

function buildStopFlyToRequest(
  routes: DayRoute[],
  waypointKey: string,
  legPoints: LatLngTuple[],
): FlyToRequest {
  const focusedWaypoint = findWaypointByKey(routes, waypointKey)?.waypoint;
  if (!focusedWaypoint) return { points: legPoints, separatingZoom: null };

  const neighborStopPositions = routes
    .flatMap((route) => route.waypoints)
    .filter((waypoint) => waypoint.key !== waypointKey && !isStartWaypoint(waypoint))
    .map((waypoint) => waypoint.position);
  const separatingZoom = getZoomToSeparateFromNeighbors(
    focusedWaypoint.position,
    neighborStopPositions,
    MAP_CLOSE_UP_ZOOM,
  );
  if (separatingZoom === null) return { points: legPoints, separatingZoom: null };
  return { points: [focusedWaypoint.position], separatingZoom };
}

interface UseWaypointFocusParams {
  dayRoutes: DayRoute[];
  displayedRoutes: DayRoute[];
  resetKey: string;
}

export function useWaypointFocus({ dayRoutes, displayedRoutes, resetKey }: UseWaypointFocusParams) {
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  const [flyToRequest, setFlyToRequest] = useState<FlyToRequest | null>(null);
  const [lastResetKey, setLastResetKey] = useState(resetKey);

  if (resetKey !== lastResetKey) {
    setLastResetKey(resetKey);
    setHoveredKey(null);
    setFocusedKey(null);
    setFlyToRequest(null);
  }

  const legPointsByKey = useMemo(() => buildLegPointsByWaypointKey(dayRoutes), [dayRoutes]);
  const displayedEntries = useMemo(() => buildJourneyEntries(displayedRoutes), [displayedRoutes]);
  const isMultiDay = displayedRoutes.length > 1;
  const focusedDate = displayedEntries.find((entry) => entry.key === focusedKey)?.day.date ?? null;
  const journeyEntries = useMemo(
    () =>
      isMultiDay
        ? displayedEntries.filter((entry) => entry.day.date === focusedDate)
        : displayedEntries,
    [isMultiDay, displayedEntries, focusedDate],
  );
  const focusedJourneyIndex =
    focusedKey === null ? -1 : journeyEntries.findIndex((entry) => entry.key === focusedKey);
  const canStepJourney = journeyEntries.length > 1;

  const handleWaypointFocus = useCallback(
    (waypointKey: string) => {
      const legPoints = legPointsByKey.get(waypointKey);
      setHoveredKey(waypointKey);
      setFocusedKey(waypointKey);
      setFlyToRequest(
        legPoints ? buildStopFlyToRequest(displayedRoutes, waypointKey, legPoints) : null,
      );
    },
    [legPointsByKey, displayedRoutes],
  );

  const handleStopFocusClear = useCallback(() => {
    setFocusedKey(null);
    setHoveredKey(null);
  }, []);

  const displayedPositions = useMemo(
    () => displayedRoutes.flatMap((route) => route.path),
    [displayedRoutes],
  );

  const handleJourneyExit = useCallback(() => {
    setHoveredKey(null);
    setFocusedKey(null);
    setFlyToRequest(
      displayedPositions.length > 0 ? { points: displayedPositions, separatingZoom: null } : null,
    );
  }, [displayedPositions]);

  const focusJourneyEntryAt = useCallback(
    (index: number) => {
      const journeyEntry = journeyEntries[index];
      if (journeyEntry) handleWaypointFocus(journeyEntry.key);
    },
    [journeyEntries, handleWaypointFocus],
  );

  const handleJourneyStart = useCallback(() => focusJourneyEntryAt(0), [focusJourneyEntryAt]);

  const handleJourneyStep = useCallback(
    (direction: JourneyStepDirection) => {
      const nextIndex = focusedJourneyIndex === -1 ? 0 : focusedJourneyIndex + direction;
      focusJourneyEntryAt(nextIndex);
    },
    [focusedJourneyIndex, focusJourneyEntryAt],
  );

  return {
    hoveredKey,
    focusedKey,
    flyToRequest,
    journeyEntries,
    isMultiDay,
    canStepJourney,
    isJourneyStepperVisible: canStepJourney && focusedKey !== null,
    handleWaypointHover: setHoveredKey,
    handleWaypointFocus,
    handleStopFocusClear,
    handleJourneyStart,
    handleJourneyStep,
    handleJourneyExit,
  };
}
